
import { profileService } from './profileService';
import { supabase } from '@/lib/supabase';
import { getUserRole } from '@/lib/supabase';
import { debugError } from '@/utils/debugLogger';

/**
 * Unified service that combines profile and role services
 * With improved error isolation for auth process
 */
export const userProfileService = {
  /**
   * Fetch user profile data with error isolation
   */
  fetchUserProfile: async (userId: string) => {
    try {
      return await profileService.fetchUserProfile(userId);
    } catch (error) {
      debugError('auth', "[userProfileService] Error fetching profile:", error);
      // Return empty profile instead of propagating error
      return { 
        name: undefined,
        avatar_url: undefined,
        dealer_activation_status: 'NONE'
      };
    }
  },
  
  /**
   * Fetch user role information with error isolation
   */
  fetchUserRole: async (userId: string) => {
    try {
      // Get role using the main roleUtils function
      const roleName = await getUserRole();
      const roleId = roleName.toLowerCase();
      
      return { roleId, roleName: roleName.toUpperCase() };
    } catch (error) {
      debugError('auth', "[userProfileService] Error fetching role:", error);
      
      // Return default role instead of propagating error
      return { roleId: 'user', roleName: 'USER' };
    }
  },
  
  /**
   * Create user profile and default role with error isolation
   */
  createUserProfile: async (userId: string, email: string, name: string) => {
    // Create profile with error handling
    try {
      await profileService.createUserProfile(userId, email, name);
    } catch (profileError) {
      debugError('auth', "[userProfileService] Error creating profile:", profileError);
      // Continue without failing the entire operation
    }
    
    // Role creation is now handled by database triggers
  },
  
  /**
   * Check dealer activation request status for a user with error isolation
   */
  checkDealerActivationStatus: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('dealer_activation_requests')
        .select('status')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (error) {
        debugError('auth', "[userProfileService] Error checking dealer activation status:", error);
        return 'NONE';
      }
      
      return data?.status || 'NONE';
    } catch (error) {
      debugError('auth', "[userProfileService] Exception while checking activation status:", error);
      return 'NONE';
    }
  }
};
